import ReferralTracker from "./ReferralTracker";
import { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

// Custom hook
import useIdleStatus from "./useIdleStatus";

// Pages
import Layout from "./Layout";
import HomePage from "./HomePage";
import About from "./About";
import StudentDashboard from "./StudentDashboard";
import TutorDashboard from "./TutorDashboard";
import RoleProtectedRoute from "./RoleProtectedRoute";
import CreateUser from "./CreateUser";
import EditUser from "./EditUser";
import DeleteUser from "./DeleteUser";
import BookLesson from "./BookLesson";
import AllBookings from "./AllBookings";
import ViewUsers from "./ViewUsers";
import SetWork from "./SetWork";
import CreateQuestion from "./CreateQuestion";
import CreateTemplateQuestion from "./CreateTemplateQuestion";
import LoggingOut from "./LoggingOut";

// Auth
import ChangePasswordModal from "./ChangePasswordModal";
import { useAuth } from "./AuthContext";

// UI components / tests
import QuestionCard from "./QuestionCard";
import QuestionCardTest from "./QuestionCardTest";
import Seeder from "./Seeder";
import AssignmentBuilder from "./AssignmentBuilder";
import PreviewTemplates from "./PreviewTemplates";
import QuestionBuilder from './QuestionBuilder';
import ClassworkPage from "./ClassworkPage";
import HomeworkPage from "./HomeworkPage";
import UploadLessonResource from "./UploadLessonResource";
import ViewAssignments from "./ViewAssignments";
import StudentAssignmentViewer from "./StudentAssignmentViewer";
import TutorClassworkPage from "./TutorClassworkPage";
import TutorHelpPage from "./TutorHelpPage";
import TutorAssignmentViewer from "./TutorAssignmentViewer";
import WordedQuestionBuilder from "./WordedQuestionBuilder";
import MillionaireGame from "./MillionaireGame";
import MillionaireGameWrapper from "./MillionaireGameWrapper";
import CreateLessonFolder from "./CreateLessonFolder";
import MillionaireTutorViewer from "./MillionaireTutorViewer";
import SetHomeworkPage from "./SetHomeworkPage";
import BlockbustersGame from './BlockbustersGame';
import BlockbustersWelcome from './BlockbustersWelcome';
import ReviewWork from "./ReviewWork";
import MillionaireReview from "./MillionaireReview";
import SelfMarkedReview from "./SelfMarkedReview";
import BattleshipsGame from './BattleshipsGame';
import MillionaireLiveJoin from './MillionaireLiveJoin';
import MillionaireLiveHost from "./MillionaireLiveHost";
import MillionaireLiveGamePage from "./MillionaireLiveGamePage";
import ComplexNumberEquations, { ComplexNumberEquationsWelcome } from "./MathsInvaders/ComplexNumberEquations";
import MultiplyAndDivideDecimals from "./MathsInvaders/MultiplyAndDivideDecimals";
import StudentAssignmentViewer2 from "./StudentAssignmentViewer2";
import ReviewHomeworkPage from "./ReviewHomeworkPage";
import MillionaireMultDivDecimal from "./MillionaireMultDivDecimal";
import MillionaireLawsOfIndices from "./MillionaireLawsOfIndices";
import MillionaireAddingAndSubtractingFractions from "./MillionaireAddingAndSubtractingFractions";
import MillionairePercentages2 from "./MillionairePercentages2";
import BlockbustersCompoundInterest from "./BlockbustersCompoundInterest";
import SolvingLinearEquations from "./Self-Marked Quiz/SolvingLinearEquations";
import LawsOfIndices from "./Self-Marked Quiz/LawsOfIndices";
import StandardForm from "./Self-Marked Quiz/StandardForm";
import MedianFromAnUngroupedFrequencyTable from "./Self-Marked Quiz/MedianFromAnUngroupedFrequencyTable";
import PresentingDataInGroups from "./Self-Marked Quiz/PresentingDataInGroups";
import VolumeOfCylinders from "./Self-Marked Quiz/VolumeOfCylinders";
import AreaAndPerimeterOfCompoundShapes from "./Self-Marked Quiz/AreaAndPerimeterOfCompoundShapes";
import StemAndLeafDiagrams from "./Self-Marked Quiz/StemAndLeafDiagrams";
import VideoLessonRoom from "./VideoLessonRoom";
import CreateLesson from "./CreateLesson";
import MathsRacer from "./MathsRacer";
import ExpandingSingleBrackets from "./MathsRacer/ExpandingSingleBrackets";
import CrosswordLcmHcf from "./CrosswordLcmHcf";

const Entry3CommunityEvents = lazy(() => import("./Functional Skills/Entry 3 Paper Community Events"));
const Entry2CharityShop = lazy(() => import("./Functional Skills/Entry 2 Paper Charity Shop"));
const AreaAndCircumferenceOfCircles = lazy(() => import("./Whack A Ghost/AreaAndCircumferenceOfCircles"));
const WhackAGhost = lazy(() => import("./Whack A Ghost/WhackAGhost"));
const PercentageChangeBowling = lazy(() => import("./MathsBowling/PercentageChange"));

function MathsShootoutStemAndLeaf() {
  useEffect(() => {
    window.location.replace("/maths-shootout/stemandleaf.html");
  }, []);
  return null;
}

function MathsShootoutMultiplyAndDividingDecimals() {
  useEffect(() => {
    window.location.replace("/maths-shootout/multiplyanddividingdecimals.html");
  }, []);
  return null;
}

function AppWrapper() {
  useIdleStatus();

  const { mustChangePassword, loading, role, currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser && !mustChangePassword) {
      if (role === "student" && location.pathname === "/") {
        navigate("/student-dashboard");
      } else if (role === "tutor" && location.pathname === "/") {
        navigate("/tutor-dashboard");
      }
    }
  }, [currentUser, mustChangePassword, role, location.pathname, navigate]);

  useEffect(() => {
    const auth = getAuth();
    let removeUnloadHandler = () => {};

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        const lastUid = localStorage.getItem("lastUid");
        if (lastUid) {
          await updateDoc(doc(db, "users", lastUid), {
            status: "offline",
            lastActive: serverTimestamp(),
          });
        }
        return;
      }

      localStorage.setItem("lastUid", user.uid);

      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        status: "online",
        lastLogin: serverTimestamp(),
      });

      const handleUnload = () => {
        updateDoc(userRef, {
          status: "offline",
          lastActive: serverTimestamp(),
        });
      };

      window.addEventListener("beforeunload", handleUnload);
      removeUnloadHandler = () =>
        window.removeEventListener("beforeunload", handleUnload);
    });

    return () => {
      unsubscribe();
      removeUnloadHandler();
    };
  }, []);

  if (loading) {
    return (
      <div
        style={{
          padding: "2rem",
          fontFamily: "Poppins",
          textAlign: "center",
          fontSize: "1.2rem",
        }}
      >
        🔄 Loading...
      </div>
    );
  }

  return (
    <>
      {mustChangePassword && <ChangePasswordModal />}

      <Routes>
        {/* Testing */}
        <Route path="/question-card" element={<QuestionCardTest />} />

        {/* Public pages */}
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<About />} />

        {/* Student dashboard */}
        <Route
          path="/student-dashboard"
          element={
            <RoleProtectedRoute role="student">
              <StudentDashboard />
            </RoleProtectedRoute>
          }
        />

        {/* Tutor dashboard and tools */}
        <Route
          path="/tutor-dashboard"
          element={
            <RoleProtectedRoute role="tutor">
              <TutorDashboard />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/view-users"
          element={
            <RoleProtectedRoute role="tutor">
              <ViewUsers />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/create-user"
          element={
            <RoleProtectedRoute role="tutor">
              <CreateUser />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/edit-user"
          element={
            <RoleProtectedRoute role="tutor">
              <EditUser />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/delete-user"
          element={
            <RoleProtectedRoute role="tutor">
              <DeleteUser />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/bookings/create"
          element={
            <RoleProtectedRoute role="tutor">
              <BookLesson />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/all-bookings"
          element={
            <RoleProtectedRoute role="tutor">
              <AllBookings />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/set-work"
          element={
            <RoleProtectedRoute role="tutor">
              <SetWork />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/create-question"
          element={
            <RoleProtectedRoute role="tutor">
              <CreateQuestion />
            </RoleProtectedRoute>
          }
        />

        {/* ✅ NEW route for parameterized question builder */}
        <Route
          path="/create-template-question"
          element={
            <RoleProtectedRoute role="tutor">
              <CreateTemplateQuestion />
            </RoleProtectedRoute>
          }
        />

        {/* Logout screen */}
        <Route path="/logging-out" element={<LoggingOut />} />
        <Route path="/seeder" element={<Seeder />} />
        <Route path="/preview-templates" element={<PreviewTemplates />} />
        <Route path="/question-builder" element={<QuestionBuilder />} />
        <Route
  path="/upload-resource"
  element={
    <RoleProtectedRoute role="tutor">
      <UploadLessonResource />
    </RoleProtectedRoute>
  }
/>

        {/* ✅ Student Assignment Pages (protected) */}
        <Route
          path="/classwork-page"
          element={
            <RoleProtectedRoute role="student">
              <ClassworkPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="/homework-page"
          element={
            <RoleProtectedRoute role="student">
              <HomeworkPage />
            </RoleProtectedRoute>
          }
        />

        {/* Assignment builder (for tutors) */}
        <Route
          path="/assignment-builder"
          element={
            <RoleProtectedRoute role="tutor">
              <AssignmentBuilder />
            </RoleProtectedRoute>
          }
        />

<Route
  path="/view-assignments"
  element={
    <RoleProtectedRoute role="tutor">
      <ViewAssignments />
    </RoleProtectedRoute>
  }
/>

<Route
  path="/student-assignment/:assignmentId"
  element={
    <RoleProtectedRoute role="student">
      <StudentAssignmentViewer />
    </RoleProtectedRoute>
  }
/>


<Route
  path="/tutor-classwork-page"
  element={
    <RoleProtectedRoute role="tutor">
      <TutorClassworkPage />
    </RoleProtectedRoute>
  }
/>

<Route
  path="/tutor-help-page"
  element={
    <RoleProtectedRoute role="tutor">
      <TutorHelpPage />
    </RoleProtectedRoute>
  }
/>

<Route
  path="/worded-question-builder"
  element={
    <RoleProtectedRoute role="tutor">
      <WordedQuestionBuilder />
    </RoleProtectedRoute>
  }
/>

<Route path="/tutor-assignment-viewer/:assignmentId/:studentUID" element={<TutorAssignmentViewer />} />

<Route path="/homework-page" element={<HomeworkPage />} />
<Route path="/millionaire-game" element={<MillionaireGame />} />
<Route path="/millionaire/:assignmentId" element={<MillionaireGameWrapper />} />
<Route path="/millionaire_tutor_viewer/:assignmentId" element={<MillionaireGameWrapper />} />
<Route path="/create-lesson-folder" element={<CreateLessonFolder />} />
<Route path="/set-homework-page" element={<SetHomeworkPage />} />
<Route path="/blockbusters-game" element={<BlockbustersGame />} />
<Route path="/blockbusters-welcome" element={<BlockbustersWelcome />} />
<Route path="/blockbusters-welcome/:assignmentId" element={<BlockbustersWelcome />} />
<Route path="/blockbusters-game/:assignmentId" element={<BlockbustersGame />} />
<Route path="/review-work" element={<ReviewWork />} />
<Route path="/blockbusters-review/:assignmentId" element={<BlockbustersGame />} />
<Route path="/millionaire-review/:assignmentId/:studentUID" element={<MillionaireReview />} />
<Route path="/self-marked-review/:assignmentId/:studentUID" element={<SelfMarkedReview />} />
<Route path="/battleships-game" element={<BattleshipsGame />} />
<Route path="/millionaire-tutor-viewer/:assignmentId" element={<MillionaireGame />} />
<Route path="/host/:assignmentId" element={<MillionaireLiveHost />} />
<Route path="/join" element={<MillionaireLiveJoin />} />
<Route path="/millionaire-live-game/:gameId" element={<MillionaireLiveGamePage />} />
<Route path="/maths-invaders" element={<ComplexNumberEquations />} />
<Route path="/maths-bowling" element={<Suspense fallback={<div role="status">Loading Maths Bowling 3D…</div>}><PercentageChangeBowling /></Suspense>} />
<Route path="/maths-invaders-multiply-and-divide-decimals" element={<MultiplyAndDivideDecimals />} />
<Route path="/maths-invaders-welcome" element={ <ComplexNumberEquationsWelcome />} />
<Route path="/student-assignment-two/:assignmentId" element={ <StudentAssignmentViewer2 />  }/>
<Route path="/review-homework" element={<ReviewHomeworkPage />} />
<Route path="/MillionaireMultDivDecimal" element={<MillionaireMultDivDecimal />} />
<Route path="/solving-linear-equations" element={<SolvingLinearEquations />} />
<Route path="/laws-of-indices" element={<LawsOfIndices />} />
<Route path="/standard-form" element={<StandardForm />} />
<Route path="/median-from-an-ungrouped-frequency-table" element={<MedianFromAnUngroupedFrequencyTable />} />
<Route path="/presenting-data-in-groups" element={<PresentingDataInGroups />} />
<Route path="/volume-of-cylinders" element={<VolumeOfCylinders />} />
<Route path="/area-and-perimeter-of-compound-shapes" element={<AreaAndPerimeterOfCompoundShapes />} />
<Route path="/stem-and-leaf-diagrams" element={<StemAndLeafDiagrams />} />
<Route path="/stemandleaf" element={<MathsShootoutStemAndLeaf />} />
<Route path="/multiplyanddividingdecimals" element={<MathsShootoutMultiplyAndDividingDecimals />} />
<Route path="/BlockbustersCompoundInterest" element={<BlockbustersCompoundInterest />} />
<Route path="/MillionaireLawsOfIndices" element={<MillionaireLawsOfIndices />} />
<Route path="/MillionaireAddingAndSubtractingFractions" element={<MillionaireAddingAndSubtractingFractions />} />
<Route path="/MillionairePercentages2" element={<MillionairePercentages2 />} />
<Route path="/lesson/:roomId" element={<VideoLessonRoom />} />
<Route path="/createlesson" element={<CreateLesson />} />
<Route path="/MathsRacer" element={<MathsRacer />} />
<Route path="/ExpandingSingleBrackets" element={<ExpandingSingleBrackets />} />
<Route path="/functional-skills/entry-3-community-events" element={<Suspense fallback={<div role="status">Loading Community Events…</div>}><Entry3CommunityEvents /></Suspense>} />
<Route path="/functional-skills/entry-2-charity-shop" element={<Suspense fallback={<div role="status">Loading Charity Shop…</div>}><Entry2CharityShop /></Suspense>} />
<Route path="/whack-a-ghost-area-and-circumference-of-circles" element={<Suspense fallback={<div>Loading circles game…</div>}><AreaAndCircumferenceOfCircles /></Suspense>} />
<Route path="/whack-a-ghost" element={<Suspense fallback={<div>Loading Whack a Ghost?</div>}><WhackAGhost /></Suspense>} />
<Route path="/CrosswordLcmHcf" element={<CrosswordLcmHcf />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <ReferralTracker />
      <AppWrapper />
    </Router>
  );
}

export default App;
